/**
 * A generic React component for making a table with sortable columns.
 *
 * Example:
 *
 *     const data = [
 *         {firstName: "Yuri", lastName: "Gagarin"},
 *         {firstName: "Marc", lastName: "Garneau"},
 *         {firstName: "Buzz", lastName: "Aldrin"},
 *         {firstName: "Roberta", lastName: "Bondar"},
 *         {firstName: "Buzz", lastName: "Lightyear"},
 *     ];
 *
 *     <Table data={data}>
 *         <Column label="First name" sortKey="firstName">
 *             {(row) => <div style={{color: "red"}}>
 *                 {row.firstName}
 *             </div>}
 *         </Column>
 *         <Column label="Last name" sortKey="lastName">
 *             {(row) => <div style={{color: "blue"}}>
 *                 {row.lastName}
 *             </div>}
 *         </Column>
 *     </Table>
 *
 *     See fixture for comprehensive examples.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";

import Column from "./column.ts";
import FilterTabs from "./filter-tabs.tsx";
import SortableTable from "./sortable-table.tsx";
import type {
    CommonTableProps,
    DescriptorId,
    FilterDescriptor,
    FilterProps,
} from "./types.ts";
import {cellPadding, transformFilterProps} from "./utils.ts";

export {Column};

export class Filter extends React.Component<FilterProps> {
    // @ts-expect-error - TS2564 - Property 'type' has no initializer and is not definitely assigned in the constructor.
    type: {
        name: string;
    };

    render(): React.ReactNode {
        return null;
    }
}

type TableChild =
    | React.ReactElement<React.ComponentProps<typeof Column>>
    | React.ReactElement<React.ComponentProps<typeof Filter>>;

// MaybeTableChild
//
// Either a TableChild or false. This type enables constructs like:
//
// {showFilters &&
//     <Filter ...>
// }
//
// where the child will be false if showFilters is false.
//
// React.Children.toArray() filters out such values.

type MaybeTableChild = TableChild | false | Array<MaybeTableChild>;

type TableProps = CommonTableProps & {
    // Components describing how to render each column/filter.
    children?: MaybeTableChild;
    // The title of the table.
    title?: string;
    // A function that, given the title text, renders the title of the table.
    customTitleRenderer?: (title: string) => React.ReactNode;
    // The description for the table. It appears below the title.
    description?: string | React.ReactElement<any>;
    // The id of the filter that will be active on initial render.
    // If filters are present and no explicit default is provided,
    // the first filter will be used as the implicit default.
    defaultFilter?: string;
    // The function that generates the "show all" text.
    // Only useful when showFirstN is specified.
    showAllText?: (n: number) => string;
    // The text on the button which displays after user clicks "show all",
    // in order to collapse the table back to showing fewer rows.
    // Only relevant when showFirstN is specified.
    showLessText?: string;
    // The color of the selected filter tab defaults to Wonder Blocks Blue.
    // TODO(nada/mdr): This prop is used in SAT, where we still use a custom color
    // palette instead of Wonder Blocks colors. Once we migrate SAT to the GTP
    // platform in late 2019, we can probably remove this prop. (TP-3207)
    selectedFilterColor: string;
    // If specified, displays only the first N rows of the table on initial
    // render. Not compatible with pagination (pageIndex and pageSize props).
    showFirstN?: number;
    // Set pageIndex and pageSize to enable pagination. Not compatible with
    // showFirstN.
    pageIndex?: number;
    pageSize?: number;
};

type DefaultProps = {
    hasTopAndBottomBorders: TableProps["hasTopAndBottomBorders"];
    rowHeight: TableProps["rowHeight"];
    selectedFilterColor: TableProps["selectedFilterColor"];
    showAllText: TableProps["showAllText"];
    useCursorPointerOnHover: TableProps["useCursorPointerOnHover"];
};

type TableState = {
    // The id of the filter that is currently active.
    activeFilter: DescriptorId | null | undefined;
    // Whether to show all of the table's rows or only the first N.
    showAllRows: boolean;
};

export default class Table extends React.Component<TableProps, TableState> {
    static defaultProps: DefaultProps = {
        rowHeight: 48,
        hasTopAndBottomBorders: true,
        useCursorPointerOnHover: true,
        showAllText: (n: number) => t`Show all`,
        selectedFilterColor: color.blue,
    };

    constructor(props: TableProps) {
        super(props);
        const {showFirstN} = props;
        let showAllRows = true;
        if (showFirstN && props.data.length > showFirstN) {
            // we want to display the "show all rows" button.
            showAllRows = false;
        }

        this.state = {
            activeFilter: null,
            showAllRows,
        };
    }

    handleFilterChange: (filterId: DescriptorId) => void = (
        filterId: DescriptorId,
    ) => {
        const {onFilterChanged} = this.props;

        if (onFilterChanged) {
            onFilterChanged(filterId.toString());
        }

        this.setState({activeFilter: filterId});
    };

    renderFilters(
        filters: Array<FilterDescriptor>,
        activeFilter?: DescriptorId | null,
    ): React.ReactNode {
        return (
            <FilterTabs
                activeTab={activeFilter?.toString() || ""}
                activeTabColor={this.props.selectedFilterColor}
                onTabChange={this.handleFilterChange}
                tabs={filters.map(({id, label, testId}) => ({
                    id: id.toString(),
                    label,
                    testId,
                }))}
            />
        );
    }

    render(): React.ReactNode {
        const {
            title,
            customTitleRenderer,
            description,
            showAllText,
            showFirstN,
            showLessText,
            children,
            defaultFilter,
            data,
            pageIndex,
            pageSize,
            ...sortableTableProps
        } = this.props;
        const {showAllRows} = this.state;

        let displayedData = data.slice();

        let columns = React.Children.toArray(children).filter(
            // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
            (child) => child.type === Column,
        );

        const filters = React.Children.toArray(children)
            // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
            .filter((child) => child.type === Filter)
            .map((child, filterIndex) =>
                // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
                transformFilterProps(child.props, filterIndex),
            );

        let activeFilter = this.state.activeFilter;
        if (filters.length && activeFilter == null) {
            activeFilter = defaultFilter || filters[0].id;
        }

        // Filter out columns that should be hidden due to the current filter.
        if (activeFilter) {
            columns = columns.filter((column) =>
                // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
                column.props.isVisibleWithFilter
                    ? // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
                      column.props.isVisibleWithFilter(activeFilter)
                    : true,
            );
        }

        // Get the active filter, and search functions
        let filterFn;
        if (activeFilter != null) {
            for (const filter of filters) {
                if (filter.id === activeFilter) {
                    filterFn = filter.fn;
                }
            }
        }

        // Apply the active filter, and search functions
        if (filterFn) {
            displayedData = displayedData.filter(filterFn);
        }

        const areRowsToHide = showFirstN && displayedData.length > showFirstN;
        const shouldHideRows = showFirstN && !showAllRows && areRowsToHide;

        const numAllRows = displayedData.length;

        // Render everything above the table
        let header;
        // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
        const hasHeaders = columns.some((column) => column.props.label);

        const titleRenderer = customTitleRenderer
            ? customTitleRenderer
            : (titleText: string) => {
                  return (
                      <div className={css(styles.defaultTitle)}>
                          {titleText}
                      </div>
                  );
              };

        const includeHeaderStyles = filters.length && hasHeaders;

        if (title || filters.length) {
            header = (
                <div
                    className={css(
                        styles.header,
                        includeHeaderStyles && styles.headerWithFilters,
                        // HACK (josh): When we're alternating background
                        // colors, the top row has a light grey background.
                        // To avoid having an awkward gap, we want to omit the
                        // bottom margin in this case.
                        includeHeaderStyles &&
                            !sortableTableProps.alternatingBackgroundVariant &&
                            styles.marginBelowFilters,
                    )}
                >
                    <div
                        className={css(
                            title ? styles.titleContainer : styles.hidden,
                        )}
                    >
                        {title && titleRenderer(title)}
                    </div>
                    <div className={css(styles.rightcorner)}>
                        {!!filters.length &&
                            this.renderFilters(filters, activeFilter)}
                    </div>
                </div>
            );
        }
        let subHeader;
        if (description) {
            subHeader = (
                <div className={css(styles.description)}>{description}</div>
            );
        }

        // optional show all/less buttons, when showFirstN is specified
        const showAllButton = showAllText && shouldHideRows && (
            <Button
                kind="tertiary"
                onClick={() => this.setState({showAllRows: true})}
                style={styles.showAllLessButton}
            >
                {showAllText(numAllRows)}
            </Button>
        );
        const showLessButton = showLessText &&
            !shouldHideRows &&
            areRowsToHide && (
                <Button
                    kind="tertiary"
                    onClick={() => this.setState({showAllRows: false})}
                    style={styles.showAllLessButton}
                >
                    {showLessText}
                </Button>
            );

        // This pertains to the showFirstN feature, which is not compatible with
        // pagination, so if pagination is enabled, this will be undefined.
        const maxRowsToShow = pageSize
            ? undefined
            : showAllRows
              ? displayedData.length
              : showFirstN;

        return (
            <div className="box-sizing-border-box-reset">
                {header}
                {subHeader}
                <SortableTable
                    {...sortableTableProps}
                    data={displayedData}
                    maxRows={maxRowsToShow}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                >
                    {/* @ts-expect-error - TS2322 - Type '(string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal)[]' is not assignable to type 'Column | ReactElement<ColumnProps, string | JSXElementConstructor<any>> | ReactElement<ColumnProps, string | JSXElementConstructor<...>>[] | undefined'. */}
                    {columns}
                </SortableTable>
                {!!(showAllButton || showLessButton) && (
                    <div className={css(styles.showAllRows)}>
                        {showAllButton}
                        {showLessButton}
                    </div>
                )}
            </div>
        );
    }
}

const defaultColor = color.blue;

const styles = StyleSheet.create({
    /* Header (the one above the table itself) styles */
    header: {
        display: "flex",
        margin: "0 12px",
        [mediaQueries.smOrSmaller]: {
            marginBottom: 7,
            flexDirection: "column",
        },
    },
    headerWithFilters: {
        borderBottom: `1px solid ${color.offBlack16}`,
    },
    marginBelowFilters: {
        marginBottom: 20,
    },
    defaultTitle: deprecatedTypography.subheadingDesktop,
    titleContainer: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        marginBottom: 7,
        paddingLeft: cellPadding,
        flexGrow: 1,
    },

    /* Misc styles */
    // TODO(briangenisio): This is currently larger than the title typography.
    // Address this when the time comes to need this feature:
    /* Ways to hide things */
    hidden: {
        visibility: "hidden",
    },
    description: {
        marginBottom: 24,
        padding: `0 ${cellPadding}px`,
        margin: `0 ${cellPadding}px`,
        [mediaQueries.smOrSmaller]: {
            marginLeft: 16 - cellPadding,
        },
        [mediaQueries.mdOrLarger]: {
            marginLeft: 20 - cellPadding,
        },
    },
    showAllRows: {
        marginTop: 5,
        color: defaultColor,
    },
    rightcorner: {
        display: "flex",
    },
    showAllLessButton: {
        marginLeft: spacing.small_12,
    },
});
